import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-file-unknow-line text-5xl text-foreground-300 mb-4 block"></i>
          <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-2">
            文章未找到
          </h2>
          <p className="text-foreground-500 text-sm mb-4">您访问的文章可能已被移除或链接有误</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full font-label text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = getRelatedArticles(article);

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://www.oumeizone.com/article/${article.slug}`}
        ogType="article"
        ogImage={article.image}
      />
      <SchemaOrg type="Article" article={article} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            {/* Article Header */}
            <article>
              <header className="mb-8">
                <Link
                  to={`/category/${article.category}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent-100 text-accent-700 rounded-full font-label text-xs mb-4 hover:bg-accent-200 transition-colors"
                >
                  {article.categoryName}
                </Link>
                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-timer-line"></i>
                    {article.readTime}
                  </span>
                </div>
              </header>

              {/* Featured Image */}
              <div className="rounded-lg overflow-hidden mb-10">
                <img
                  src={article.image}
                  alt={article.title}
                  title={`${article.title} - 欧美专区`}
                  className="w-full h-auto object-cover object-top"
                />
              </div>

              {/* Article Content */}
              <div
                className="prose-custom mb-12"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Tags */}
              <div className="flex flex-wrap items-center gap-2 mb-12 pb-8 border-b border-background-200">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 bg-background-100 text-foreground-600 rounded-full text-xs font-label"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </article>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <section className="pb-16">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {relatedArticles.map((related) => (
                    <ArticleCard key={related.slug} article={related} />
                  ))}
                </div>

                <div className="text-center mt-8">
                  <Link
                    to={`/category/${article.category}`}
                    className="inline-flex items-center gap-2 px-6 py-3 border border-primary-300 text-primary-600 rounded-full font-label text-sm hover:bg-primary-50 transition-colors whitespace-nowrap"
                  >
                    查看更多{article.categoryName}文章
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </section>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}